import React, { useEffect, useState, useCallback } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../../components/ui/button';
import {
  AdminModal,
  AdminInput,
  AdminSelect,
  AdminTextarea,
} from '../../components/admin/AdminUI';
import { ImageUploader } from '../../components/admin/ImageUploader';
import { Plus, Pencil, Trash2 } from 'lucide-react';

const CATEGORIES = ['Trailers', 'Generators', 'Tractors', 'Tractor Attachments', 'Lawn Care'];

const emptyForm = () => ({
  name: '',
  category: 'Trailers',
  description: '',
  specifications: '',
  daily_rate: '0',
  weekly_rate: '0',
  deposit: '0',
  image_url: '',
  available: true,
  requires_tractor: false,
});

export const AdminEquipment = () => {
  const { authedAxios } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authedAxios().get('/api/admin/equipment');
      setItems(data.equipment || []);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [authedAxios]);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setForm(emptyForm());
    setModal({ mode: 'add' });
  };

  const openEdit = (eq) => {
    setForm({
      name: eq.name || '',
      category: eq.category || 'Trailers',
      description: eq.description || '',
      specifications: eq.specifications || '',
      daily_rate: String(eq.daily_rate ?? 0),
      weekly_rate: String(eq.weekly_rate ?? 0),
      deposit: String(eq.deposit ?? 0),
      image_url: eq.image_url || '',
      available: eq.available !== false,
      requires_tractor: !!eq.requires_tractor,
    });
    setModal({ mode: 'edit', equipment: eq });
  };

  const close = () => {
    setModal(null);
    setForm(emptyForm());
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        daily_rate: parseFloat(form.daily_rate) || 0,
        weekly_rate: parseFloat(form.weekly_rate) || 0,
        deposit: parseFloat(form.deposit) || 0,
      };
      if (modal.mode === 'add') {
        await authedAxios().post('/api/admin/equipment', payload);
      } else {
        await authedAxios().patch(`/api/admin/equipment/${modal.equipment.id}`, payload);
      }
      close();
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (eq) => {
    if (!window.confirm(`Delete "${eq.name}"? This cannot be undone.`)) return;
    try {
      await authedAxios().delete(`/api/admin/equipment/${eq.id}`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Delete failed');
    }
  };

  const onField = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  if (loading) return <p className="text-[#F4F1EA]/60">Loading equipment...</p>;
  if (err) return <p className="text-red-400">{err}</p>;

  return (
    <div data-testid="admin-equipment-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-3xl font-bold text-[#F4F1EA]">Equipment</h2>
          <p className="text-[#F4F1EA]/60 mt-1">{items.length} items</p>
        </div>
        <Button
          data-testid="admin-add-equipment-btn"
          onClick={openAdd}
          className="bg-[#B8860B] hover:bg-[#B8860B]/90 text-[#F4F1EA]"
        >
          <Plus className="mr-2 h-4 w-4" /> Add Equipment
        </Button>
      </div>

      <div className="overflow-x-auto bg-[#0B132B] border border-[#B8860B]/30 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-[#050814] text-[#B8860B] uppercase tracking-wider text-xs">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Category</th>
              <th className="text-left p-3">Daily</th>
              <th className="text-left p-3">Weekly</th>
              <th className="text-left p-3">Deposit</th>
              <th className="text-left p-3">Available</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan="7" className="p-6 text-center text-[#F4F1EA]/60">
                  No equipment yet.
                </td>
              </tr>
            )}
            {items.map((eq) => (
              <tr
                key={eq.id}
                data-testid={`admin-equipment-row-${eq.id}`}
                className="border-t border-[#B8860B]/10 hover:bg-[#050814]/60"
              >
                <td className="p-3 text-[#F4F1EA]">
                  <p className="font-semibold">{eq.name}</p>
                  <p className="text-xs text-[#F4F1EA]/60 max-w-md line-clamp-1">{eq.description}</p>
                </td>
                <td className="p-3 text-[#F4F1EA]/85">{eq.category}</td>
                <td className="p-3 text-[#B8860B] font-semibold">${eq.daily_rate}</td>
                <td className="p-3 text-[#F4F1EA]/85">${eq.weekly_rate || 0}</td>
                <td className="p-3 text-[#F4F1EA]/85">${eq.deposit || 0}</td>
                <td className="p-3">
                  <span
                    className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                      eq.available
                        ? 'bg-[#B8860B] text-[#F4F1EA]'
                        : 'bg-red-900/40 text-red-300 border border-red-500/40'
                    }`}
                  >
                    {eq.available ? 'Available' : 'Hidden'}
                  </span>
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button
                    data-testid={`admin-edit-equipment-${eq.id}`}
                    onClick={() => openEdit(eq)}
                    className="inline-flex items-center px-2 py-1 text-[#B8860B] hover:bg-[#B8860B]/15 rounded mr-1"
                    title="Edit"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    data-testid={`admin-delete-equipment-${eq.id}`}
                    onClick={() => remove(eq)}
                    className="inline-flex items-center px-2 py-1 text-red-400 hover:bg-red-500/15 rounded"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AdminModal
        testid="admin-equipment-modal"
        open={!!modal}
        onClose={close}
        title={modal?.mode === 'add' ? 'Add Equipment' : 'Edit Equipment'}
        footer={
          <>
            <Button variant="outline" onClick={close} className="border-[#B8860B]/40 text-[#F4F1EA]">
              Cancel
            </Button>
            <Button
              data-testid="admin-equipment-save-btn"
              onClick={save}
              disabled={saving}
              className="bg-[#B8860B] hover:bg-[#B8860B]/90 text-[#F4F1EA]"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <AdminInput label="Name" value={form.name} onChange={onField('name')} required />
          <AdminSelect label="Category" value={form.category} onChange={onField('category')}>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </AdminSelect>
          <AdminInput label="Daily Rate ($)" type="number" step="0.01" value={form.daily_rate} onChange={onField('daily_rate')} />
          <AdminInput label="Weekly Rate ($, 7 days)" type="number" step="0.01" value={form.weekly_rate} onChange={onField('weekly_rate')} />
          <AdminInput label="Deposit ($)" type="number" step="0.01" value={form.deposit} onChange={onField('deposit')} />
        </div>
        <ImageUploader
          testid="equipment-image-uploader"
          label="Photo"
          value={form.image_url}
          onChange={(url) => setForm({ ...form, image_url: url })}
        />
        <AdminTextarea label="Description" value={form.description} onChange={onField('description')} />
        <AdminTextarea label="Specifications" value={form.specifications} onChange={onField('specifications')} />
        <div className="flex gap-6 text-[#F4F1EA]">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.available}
              onChange={onField('available')}
              className="accent-[#B8860B]"
            />
            <span>Available</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.requires_tractor}
              onChange={onField('requires_tractor')}
              className="accent-[#B8860B]"
            />
            <span>Tractor attachment (requires tractor)</span>
          </label>
        </div>
      </AdminModal>
    </div>
  );
};

export default AdminEquipment;
